import { makeStyles, Tooltip } from "@material-ui/core";
import { capitalizeFirstLetter, formatMoney, getBullflowColorPrefCB, getBullflowTimezonePref, getSentimentText, optionColorShouldBeGreenOnlyType } from "../Utils";
const tooltips = {
    SWEEP: "Executed rapidly across multiple markets simultaneously to take advantage of the best available prices.",
    BLOCK: "Executed on single exchange. Larger trades may be negotiated off the exchange.",
    SPLIT: "Executed across two markets to achieve better price realization and liquidity.",
    MULTI: "A multileg trade involving the buying or selling of multiple options in a single transaction.",
};
const bullflowColorPref = getBullflowColorPrefCB();
const bullflowLocalTimezonePref = getBullflowTimezonePref();
const BULLFLOW_GREEN = "#20d46b";
const BULLFLOW_RED = "#cc3753";
const BULLFLOW_OPACITY_GREEN = "rgba(2, 212, 44, 0.15)";
const BULLFLOW_OPACITY_RED = "rgba(250, 0, 67, 0.15)";
const CB_OPACITY_GREEN = "rgba(44, 140, 90, 0.15)";
const CB_OPACITY_RED = "rgba(211, 84, 0, 0.15)";
const CB_GREEN = "#2c8c5a";
const CB_RED = "#d35400";
const GREEN_ = bullflowColorPref ? CB_GREEN : BULLFLOW_GREEN;
const RED_ = bullflowColorPref ? CB_RED : BULLFLOW_RED;
const OPACITY_GREEN_ = bullflowColorPref ? CB_OPACITY_GREEN : BULLFLOW_OPACITY_GREEN;
const useStyles = makeStyles({
    dataTypeWhite: {
        color: 'white'
    },
    dataTypeBlue: {
        color: '#ffa42e '
    },
    logo: {
        height: '15px',
        margin: 0,
        padding: 0,
        marginRight: '10px',
        cursor: 'pointer',
    },
    dataTypeGreen: {
        color: GREEN_,
        fontWeight: 700,
    },
    dataTypeRed: {
        color: RED_,
        fontWeight: 700,
    },
});
// Configure date options based on timezone preference
const dateOptions = {
    hour12: false,
    timeZone: bullflowLocalTimezonePref ? undefined : 'America/New_York' // undefined uses local timezone
};

// Tooltip content changes based on timezone preference
const tooltipContent = bullflowLocalTimezonePref
    ? "Time of trade, local timezone"
    : "Time of trade, EST Time zone";

/**
 * Cell renderer for the "sym" column that adjusts its size to fit the text
 */
export const TickerCellRenderer = ({ data }) => {
    const ot = data?.ot;
    const optionColorShouldBeGreen_ = optionColorShouldBeGreenOnlyType(ot);
    const greenType = bullflowColorPref ? CB_GREEN : BULLFLOW_GREEN;
    const redType = bullflowColorPref ? CB_RED : BULLFLOW_RED;
    const OPACITY_GREEN_ = bullflowColorPref ? CB_OPACITY_GREEN : CB_OPACITY_GREEN;
    const OPACITY_RED_ = bullflowColorPref ? CB_OPACITY_RED : CB_OPACITY_RED;

    const wrapperStyle = {
        borderRadius: '5px',
        color: '#fff',
        backgroundColor: optionColorShouldBeGreen_ ? OPACITY_GREEN_ : OPACITY_RED_,
        borderColor: optionColorShouldBeGreen_ ? greenType : redType,
        borderWidth: '1px',
        borderStyle: 'solid',

        width: '65px',           // ← fixed width
        padding: '5px 0',        // vertical padding only
        lineHeight: '15px',
        fontSize: '11.5px',
        fontWeight: 'bold',
        textAlign: 'center',     // center the text in the box
        whiteSpace: 'nowrap',    // prevent wrapping
        overflow: 'hidden',      // hide overflow
    };

    return <div style={wrapperStyle}>{data?.tsy}</div>;
}


export const OptionTypeCellRenderer = ({ data }) => {
    const ot = data?.ot;
    const classes = useStyles();
    return (
        <div className={`${optionColorShouldBeGreenOnlyType(ot) ? classes.dataTypeGreen : classes.dataTypeRed} ${classes.cell}`}>
            {ot === "P" ? "Put" : "Call"}
        </div>
    );
}

export const UnixTimestampCellRenderer = ({ data }) => {

    return (
        <Tooltip enterNextDelay={750} enterDelay={750} title={tooltipContent}>
            <div style={{ color: '#858585' }}>
                {new Date(data?.ut * 1000).toLocaleTimeString('en-US', dateOptions)}
            </div>
        </Tooltip>
    );
};

export const PremiumCellRenderer = ({ data }) => {
    const classes = useStyles();
    return (
        <div className={`${classes.dataTypeWhite}  ${classes.cell}`}>
            {'$' + formatMoney(data?.p)}
        </div>
    );
}

export const SpreadExecutionCellRenderer = ({ data }) => {
    const executionValue = data?.se;
    const classes = useStyles();
    return (
        <Tooltip enterNextDelay={750} enterDelay={750} title={
            executionValue === "Above Ask" ? "Executed Above Ask" :
                executionValue === "Below Bid" ? "Executed Below Bid" :
                    executionValue === "Bid" ? "Executed bid side" :
                        executionValue === "Ask" ? "Executed ask side" :
                            "Executed at/near Mark"
        }>
            <div className={classes.dataTypeWhite}>
                {executionValue === "Above Ask" ? "AA" :
                    executionValue === "Below Bid" ? "BB" :
                        executionValue === "Below Bid" ? "BB" :
                            executionValue === "Below Bid" ? "BB" :
                                executionValue}
            </div>
        </Tooltip>
    )
}

export const TradeTypeCellRenderer = ({ data }) => {
    let value = data?.tt;
    // if (data?.im) {
    //     value = "MULTI";
    // }
    const style = {
        color: value === "SWEEP" ? '#e3c102' :
            value === "BLOCK" ? '#027df7' :
                value === "SPLIT" ? '#b223fa' : 'white'
    };
    const displayValue = capitalizeFirstLetter(value);

    const tooltip = tooltips[value.toUpperCase()] || "Standard trade type";
    return (
        <Tooltip  enterDelay={1250} title={tooltip}>
            <div style={style}>
                {displayValue}
            </div>
        </Tooltip>
    );
}

export const TradeSizeCellRenderer = ({data}) => {
    const size = data?.ts;
    const oi = data?.oi;
    const volume = data?.cv;
    if (oi != null && size > oi) {
        // return text that is #29ab8a greenish
        return (
            <Tooltip  enterDelay={1000} title="Trade size is greater than open interest. Unusual.">
                <div style={{ color: '#19cfa0' }}>
                    {size}
                </div>
            </Tooltip>
        );
    } else if (volume != null && oi != null && volume > oi) {
        //return text that is #ab299c purpleish
        return (
            <Tooltip enterDelay={1250} title="Contract volume is greater than open interest.">
                <div style={{ color: '#d117bb' }}>
                    {size}
                </div>
            </Tooltip>
        );
    } else {
        return (
            <div>
                {size}
            </div>
        );
    }
}


export const SigScoreCellRenderer = ({ data }) => {
    let value = data?.sc;
    if (typeof value === 'number') {
        value = Number(value.toFixed(2));
    }

    if (!value) { return null; }

    let color;
    if (value <= 0.2) {
        color = '#fa0043';
    } else if (value > 0.2 && value <= 0.3) {
        color = '#f71707';
    } else if (value > 0.3 && value <= 0.4) {
        color = '#f74f07';
    } else if (value > 0.4 && value <= 0.5) {
        color = '#f76f07';
    } else if (value > 0.5 && value <= 0.6) {
        color = '#f79307';
    } else if (value > 0.6 && value <= 0.7) {
        color = '#f7cf07';
    } else if (value > 0.7 && value <= 0.8) {
        color = '#c3f707';
    } else if (value > 0.8 && value <= 0.89) {
        color = '#73f707';
    } else if (value > 0.89 && value <= 0.95) {
        color = '#24e00b'
    } else if (value > 0.95 && value <= 1.00) {
        color = '#25ff08'
    } else {
        color = '#FFD700';
    }

    let percentage = value * 100;

    return (
        <Tooltip enterNextDelay={750} enterDelay={750} title="Indicates how unusual the trade is. The closer to 1.0, the more unusual.">
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
            }}>
                <div style={{
                    width: '80%',
                    height: '8px',
                    borderRadius: '10px',
                    background: '#15111f',
                    fontSize: '0.65em',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <div style={{
                        width: `${percentage}%`,
                        height: '10px',
                        backgroundColor: color,
                        borderRadius: '10px',
                        marginRight: '2px'
                    }}>
                        {value}
                    </div>
                </div>
            </div>
        </Tooltip>
    );
}

export const TickerSentimentCellRenderer = ({data}) => {
    const value = data?.tks;
    const classes = useStyles();
    return (
        <div className={value === 1 ? classes.dataTypeGreen :
            value === -1 ? classes.dataTypeRed :
                classes.dataTypeWhite}>
            {getSentimentText(value)}
        </div>
    )
}




// const UnixTimestampCellRenderer = (params) => {
//     const value = params?.value;

//     // Configure date options based on timezone preference
//     const dateOptions = {
//         hour12: false,
//         timeZone: bullflowLocalTimezonePref ? undefined : 'America/New_York' // undefined uses local timezone
//     };

//     // Tooltip content changes based on timezone preference
//     const tooltipContent = bullflowLocalTimezonePref
//         ? "Time of trade, local timezone"
//         : "Time of trade, EST Time zone";

//     return (
//         <Tooltip closeDelay={1} delay={1250} content={tooltipContent}>
//             <div style={{ color: '#858585' }}>
//                 {new Date(value * 1000).toLocaleTimeString('en-US', dateOptions)}
//             </div>
//         </Tooltip>
//     );
// };